<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { APP_EVENTS, onAppEvent } from '@/Utils/appEventBus'
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification"
import _ from 'lodash'
import { sortStreams } from '@/utils'
import { usePersistedSort, DATE_NAME_SORT } from '@/Utils/usePersistedSort'
import Spinner from "@/components/ui/Spinner.vue"
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue'
import StreamCardView from '@/components/ui/StreamCardView.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import AddTranscoderModal from "@/components/modals/AddTranscoderModal.vue"
import TranscoderService from '@/services/TranscoderService'
import IconBase from '../../components/icon/IconBase.vue'
import IconPlus from '../../components/icon/IconPlus.vue'
import IconTranscoder from '../../components/icon/IconTranscoder.vue'
import IconLock from '../../components/icon/IconLock.vue'
import FormButton from '../../components/Atoms/FormButton.vue'
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue'
import IconBookOpen from '../../components/icon/IconBookOpen.vue'
import IconPlay from '../../components/icon/IconPlay.vue'
import IconChevronUpDown from '../../components/icon/IconChevronUpDown.vue'
import IconCheckmark from '../../components/icon/IconCheckmark.vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ViewModeSwitcher from '@/components/ui/ViewModeSwitcher.vue'
import ResponsiveToolbar from '@/components/ui/ResponsiveToolbar.vue'
import ListingSearchInput from '@/components/ui/ListingSearchInput.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import ListingCreateTile from '@/components/ui/ListingCreateTile.vue'
import { planLimitReachedMessage } from '@/Utils/planLimitCopy'

const store = useStore()
const router = useRouter()
const { notify } = useNotification()
const source = ref(null)

// Refs
const deleteProcessing = ref(false),
  selectedTranscoder = ref(null)

// Computed
const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])
const maxTranscoders = computed(() => store.getters['User/maxTranscoders'])
const transcoders = computed(() => store.getters['Transcoders/transcoders'])
const shouldShowCompactModeStreams = computed(() => store.getters['Ui/shouldShowCompactModeStreams'])
const searchQuery = ref('')
const { sortBy, setSort } = usePersistedSort('castr.sort.transcoder', DATE_NAME_SORT, 'Newest')
const sortDropdown = ref(null)
const sortByLabel = computed(() => sortBy.value)
const showListControls = computed(() => (transcoders.value?.length ?? 0) > 2)

const transcoderLimit = computed(() => {
  const c = maxTranscoders.value
  return typeof c === 'number' && c > 0 ? c : null
})

const transcoderHeaderUsage = computed(() => {
  if (!transcoders.value?.length) return null
  const meta = {
    used: transcoders.value.length,
    label: 'transcoders',
    labelSingular: 'transcoder',
  }
  if (transcoderLimit.value) meta.limit = transcoderLimit.value
  return meta
})

const atTranscoderLimit = computed(() => {
  const max = transcoderLimit.value
  if (!max) return false
  return (transcoders.value?.length ?? 0) >= max
})

const transcoderCreateDisabledTitle = computed(() => {
  if (atTranscoderLimit.value) return planLimitReachedMessage('Transcoder')
  return ''
})

const streamFilter = computed(() => {
  const tmp = _.cloneDeep(transcoders.value)
  const mapped = _.map(tmp, (i) => {
    i.platforms = []
    i.sharedUsers = []
    i.creationTime = i.creationTime || i.createdAt || i._id
    return i
  })
  const searchStr = searchQuery.value.toLowerCase()
  const filtered = mapped.filter((item) => (item.name || '').toLowerCase().includes(searchStr))
  return sortStreams(filtered, sortBy.value) || filtered
})

// Hooks
onMounted(async () => {
  store.dispatch('Ui/setLoadingStatus', true)
  await store.dispatch('User/loadUser')
  await store.dispatch('Transcoders/getTranscoders')
  store.dispatch('Ui/setLoadingStatus', false)
})

onBeforeUnmount(onAppEvent(APP_EVENTS.TOGGLE_FINISHED, () => {
  store.dispatch('Transcoders/getTranscoders', true)
}))

// Methods
const onTranscoderDeleteReq = (transcoder) => {
  selectedTranscoder.value = transcoder
  showModal('delete-transcoder')
}

const openAddTranscoderModal = () => {
  showModal('modal-add-transcoder')
}

const changeSortBy = (val) => {
  setSort(val)
  sortDropdown.value?.toggleDropdown?.()
}

const showDemoModal = () => {
  showModal('modal-demo-video')
}

const onTranscoderDeleteConfirm = async () => {
  deleteProcessing.value = true
  try {
    await TranscoderService.deleteTranscoder(selectedTranscoder.value._id)
    // Force refresh to remove deleted transcoder from list
    await store.dispatch('Transcoders/getTranscoders', true)
    notify({
      group: 'success',
      text: 'Transcoder profile deleted'
    })
  } catch (err) {
    console.error(err)
    notify({
      group: 'error',
      text: 'Unable to delete the transcoder.'
    })
  }

  deleteProcessing.value = false
}

const onNewTranscoder = async (transcoder) => {
  // Redirect immediately for seamless UX
  router.push({ path: '/transcoder/' + transcoder._id })
  // Refresh store in background
  store.dispatch('Transcoders/getTranscoders', true)
}

const getTooltipLinkedSourceText = (stream) => {
  const linkedStream = stream?.destination
  if (linkedStream) {
    const linkedStreamName = linkedStream.name
    return `This livestream cannot be deleted because it is being used as a source in ${linkedStreamName.length > 30 ? linkedStreamName.slice(0, 30) + '...' : linkedStreamName} livestream. To delete this livestream, first remove it as a source from ${linkedStreamName.length > 30 ? linkedStreamName.slice(0, 30) + '...' : linkedStreamName} livestream.`
  }
  return ''
}

</script>

<template>
  <div class="container detail-shell px-4">
    <PageHeader
      title="Transcoder"
      subtitle="Transcode and convert your streams to the formats you need."
      mobile-inline
      :usage="transcoderHeaderUsage"
    >
      <Tooltip :active="atTranscoderLimit" wrap-trigger :html="true" :hide="1000">
        <FormButton v-if="!loadingStatus && hasLiveSubscription?.enabled" class="gap-x-2" size="md"
          :disabled="atTranscoderLimit" @click="openAddTranscoderModal">
          <IconBase>
            <IconPlus />
          </IconBase>
          New <span class="hidden md:block">transcoder</span>
        </FormButton>
        <template #content>
          <span class="text-surface-8 text-sm" v-html="transcoderCreateDisabledTitle" />
        </template>
      </Tooltip>
    </PageHeader>
    <Spinner v-if="loadingStatus" text="Loading your transcoders…" classes="text-surface-8 mt-48"
      spinner-color="var(--c-surface-8)" spinner-size="15px" />
    <div v-else-if="hasLiveSubscription?.enabled"
      :class="{ 'container--transcoder': !(transcoders.length > 0) }">
      <section v-if="transcoders.length > 0" class="mt-5">
        <ResponsiveToolbar class="mb-3">
          <template v-if="showListControls">
            <DropDownMenu ref="sortDropdown" type="secondary" position="left">
              <template #toggle-button>
                <span class="text-surface-8">Sort by:&nbsp;</span>
                <span class="text-white">{{ sortByLabel }}</span>
                <icon-base class="!ml-1 shrink-0 text-surface-8">
                  <icon-chevron-up-down />
                </icon-base>
              </template>
              <template #menu-items>
                <DropDownMenuItem :active="sortBy === 'Oldest'" @click="changeSortBy('Oldest')">
                  Oldest
                  <icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Oldest'" fill="none">
                    <icon-checkmark />
                  </icon-base>
                </DropDownMenuItem>
                <DropDownMenuItem :active="sortBy === 'Newest'" @click="changeSortBy('Newest')">
                  Newest
                  <icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Newest'" fill="none">
                    <icon-checkmark />
                  </icon-base>
                </DropDownMenuItem>
                <DropDownMenuItem :active="sortBy === 'A to Z'" @click="changeSortBy('A to Z')">
                  A to Z
                  <icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'A to Z'" fill="none">
                    <icon-checkmark />
                  </icon-base>
                </DropDownMenuItem>
                <DropDownMenuItem :active="sortBy === 'Z to A'" @click="changeSortBy('Z to A')">
                  Z to A
                  <icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Z to A'" fill="none">
                    <icon-checkmark />
                  </icon-base>
                </DropDownMenuItem>
              </template>
            </DropDownMenu>
            <ListingSearchInput v-model="searchQuery" />
          </template>
          <ViewModeSwitcher class="shrink-0" />
        </ResponsiveToolbar>
        <ul class="grid gap-4 auto-rows-fr"
          :class="shouldShowCompactModeStreams ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'">
          <li v-for="stream in streamFilter" :key="stream._id">
            <stream-card-view :stream="stream" :showSubstreams="false" :compactMode="shouldShowCompactModeStreams" :lightMode="false"
              :previewMode="false" :check="false" :storage="true" :transcoderView="true"
              :hasEnabledLinkedSources="!!stream?.destination"
              :tooltipLinkedSourceText="getTooltipLinkedSourceText(stream)" @delete-stream="onTranscoderDeleteReq"
              :deleteBlock="deleteProcessing" />
          </li>
          <li v-if="!searchQuery">
            <ListingCreateTile
              label="transcoder"
              :view="shouldShowCompactModeStreams ? 'list' : 'thumbnail'"
              :used="transcoders.length"
              :limit="transcoderLimit"
              :limit-reached="atTranscoderLimit"
              @create="openAddTranscoderModal"
            />
          </li>
        </ul>
        <confirm-modal message="Would you like to delete this transcoder?" modal-id="delete-transcoder"
          modal-type="danger" @modal-confirm="onTranscoderDeleteConfirm" />
      </section>
      <EmptyState v-else title="Transcode your stream"
        subtitle="Convert resolution, frame rate, and codec, then route the output to one of your streams. For example, downscale 1080p to 720p or deinterlace it.">
        <template #icon>
          <IconBase width="28" height="28" fill="none">
            <IconTranscoder />
          </IconBase>
        </template>
        <template #actions>
          <div class="flex flex-col w-[250px] gap-2 mx-auto">
            <FormButton type="secondary" class="gap-2 justify-center w-full"
              href="https://docs.castr.com/en/articles/8702304-how-to-transcode-your-stream" target="_blank">
              <IconBase>
                <IconBookOpen />
              </IconBase>
              How Transcoder works
            </FormButton>
            <FormButton type="secondary" class="gap-2 justify-center w-full" @click="showDemoModal">
              <IconBase>
                <IconPlay />
              </IconBase>
              Transcoder Demo
            </FormButton>

            <Tooltip :active="atTranscoderLimit" wrap-trigger :html="true" :hide="1000">
              <FormButton class="ml-auto gap-x-2 justify-center w-full" :disabled="atTranscoderLimit" @click="openAddTranscoderModal">
                <IconBase>
                  <IconPlus />
                </IconBase>
                New transcoder
              </FormButton>
              <template #content>
                <span class="text-surface-8 text-sm" v-html="transcoderCreateDisabledTitle" />
              </template>
            </Tooltip>
          </div>
        </template>
      </EmptyState>
    </div>
    <div v-else class="page-title-area">
      <EmptyState title="Upgrade to unlock Transcoder"
        subtitle="Convert resolution, frame rate, and codec, then route the output to one of your streams. It's not included in your current plan.">
        <template #icon>
          <IconBase width="28" height="28" fill="none">
            <IconLock />
          </IconBase>
        </template>
        <template #actions>
          <div class="flex flex-col w-[250px] gap-2 mx-auto">
            <FormButton type="secondary" class="gap-2 justify-center w-full"
              href="https://docs.castr.com/en/articles/8702304-how-to-transcode-your-stream" target="_blank">
              <IconBase>
                <IconBookOpen />
              </IconBase>
              How Transcoder works
            </FormButton>
            <FormButton type="secondary" class="gap-2 justify-center w-full" @click="showDemoModal">
              <IconBase>
                <IconPlay />
              </IconBase>
              Transcoder Demo
            </FormButton>
            <FormButton class="ml-auto gap-x-2 justify-center w-full" to="/subscribe">
              Upgrade Plan
            </FormButton>
          </div>
        </template>
      </EmptyState>
    </div>
  </div>
  <demo-video-modal videoid="0MPTCZa5OJIYRebzIe6pY" />
  <add-transcoder-modal @transcoder-added="onNewTranscoder" />
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->

<style scoped></style>
